"use client";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  User,
  Phone,
  Hash,
  Calendar,
  Lock,
  LogOut,
  Settings,
  Clock,
  DollarSign,
  Layers,
  Hourglass,
} from "lucide-react";
import { useAuthStore } from "../store/authStore";
import api from "../services/api";
import toast from "react-hot-toast";
// Assuming these modals exist and are correctly implemented
import ChangePasswordModal from "../components/ChangePasswordModal";

interface PlanDetails {
  id: string;
  planName: string;
  noOfCompany: number;
  planType: string;
  duration: number;
  amount: number;
  formattedAmount: string;
  formattedDuration: string;
  startDate: string;
  endDate: string;
  status: string;
  isActive: boolean;
}

interface UserProfile {
  id: string;
  company_name: string;
  email: string;
  mobile: string;
  customer_type: string;
  gstNo: string;
  tally_serialno: string;
  tally_version: string;
  category?: string;
  created_at: string;
  plan?: PlanDetails; // Added plan details
}

export default function ProfilePage() {
  const { user: authUser, logout } = useAuthStore();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      // Corrected API endpoint to match authController.js's getProfile route
      const response = await api.get("/auth/profile");
      if (response.data.user) {
        setUser(response.data.user);
        // Log the user object to inspect its content, especially _id
        console.log("Fetched user profile:", response.data.user);
      } else {
        toast.error("User data not found in response.");
      }
    } catch (error: any) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
      logout();
      toast.success("Logged out successfully!");
    } catch (error: any) {
      console.error("Error logging out:", error);
      toast.error("Failed to logout");
    }
  };

  // Function to format date to India Timestamp (DD/MM/YYYY)
  const formatIndianDate = (dateString: string | undefined) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN"); // DD/MM/YYYY
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          Profile Settings
        </h1>
        <p className="text-gray-600">
          Manage your account information and security settings
        </p>
      </motion.div>

      {/* Main Profile and Plan Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Overview Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2 bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200"
        >
          <div className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 px-8 py-12">
            <div className="flex items-center space-x-6">
              <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-4 border-white/30">
                <span className="text-4xl font-bold text-white">
                  {user?.company_name?.charAt(0)?.toUpperCase() || "U"}
                </span>
              </div>
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-white mb-2">
                  {user?.company_name}
                </h2>
                <p className="text-blue-100 text-lg mb-3">{user?.email}</p>
                <div className="flex items-center space-x-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 text-white border border-white/30">
                    <User size={14} className="mr-1" />
                    {user?.customer_type} Customer
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 text-white border border-white/30">
                    <Clock size={14} className="mr-1" />
                    Since{" "}
                    {user?.created_at
                      ? new Date(user.created_at).getFullYear()
                      : "N/A"}
                  </span>
                </div>
              </div>
            </div>
            {/* Action Buttons */}
            <div className="absolute top-6 right-6 flex space-x-3">
              <button
                onClick={() => setShowPasswordModal(true)}
                className="p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all duration-200 border border-white/30"
                title="Change Password"
              >
                <Lock size={20} className="text-white" />
              </button>
            </div>
          </div>
          {/* Profile Information Details */}
          <div className="p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Settings size={20} className="mr-2 text-gray-600" />
              General Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { icon: Phone, label: "Mobile Number", value: user?.mobile },
                { icon: Hash, label: "GST Number", value: user?.gstNo },
                {
                  icon: User,
                  label: "Category",
                  value: user?.category || "Not Specified",
                },
                {
                  icon: Hash,
                  label: "Tally Serial No",
                  value: user?.tally_serialno,
                },
                {
                  icon: Calendar,
                  label: "Tally Version",
                  value: user?.tally_version,
                },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + index * 0.05 }}
                    className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <Icon size={20} className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-500 mb-1">
                        {item.label}
                      </p>
                      <p className="text-lg font-semibold text-gray-900 truncate">
                        {item.value || "Not specified"}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Plan Details Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-1 bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 p-8 flex flex-col"
        >
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Layers size={24} className="mr-2 text-purple-600" />
            Your Current Plan
          </h3>
          {user?.plan ? (
            <div className="space-y-4 flex-grow">
              <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                <span className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <DollarSign size={20} className="text-purple-600" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-600">Plan Name</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {user.plan.planName}
                  </p>
                </div>
              </div>
              <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                <span className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <DollarSign size={20} className="text-purple-600" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-600">Amount</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {user.plan.formattedAmount}
                  </p>
                </div>
              </div>
              <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                <span className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <Hourglass size={20} className="text-purple-600" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-600">Duration</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {user.plan.formattedDuration}
                  </p>
                </div>
              </div>
              <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                <span className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <Calendar size={20} className="text-purple-600" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Start Date
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatIndianDate(user.plan.startDate)}
                  </p>
                </div>
              </div>
              <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                <span className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <Calendar size={20} className="text-purple-600" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-600">End Date</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatIndianDate(user.plan.endDate)}
                  </p>
                </div>
              </div>
              <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                <span className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <Clock size={20} className="text-purple-600" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-600">Status</p>
                  <p className="text-lg font-semibold text-gray-900 capitalize">
                    {user.plan.status}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No active plan found.</p>
              <p className="text-gray-400 text-sm">
                Contact support to subscribe to a plan.
              </p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        <button
          onClick={() => setShowPasswordModal(true)}
          className="group p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-green-200 flex items-center justify-between"
        >
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors mr-4">
              <Lock size={24} className="text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Change Password
              </h3>
              <p className="text-gray-600 text-sm">
                Update your account password for better security
              </p>
            </div>
          </div>
          <div className="w-2 h-2 bg-green-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
        <button
          onClick={handleLogout}
          className="group p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-red-200 flex items-center justify-between"
        >
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors mr-4">
              <LogOut size={24} className="text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Logout
              </h3>
              <p className="text-gray-600 text-sm">
                Sign out from your account securely
              </p>
            </div>
          </div>
          <div className="w-2 h-2 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      </motion.div>

      {/* Account Details (User ID & Created At) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200"
      >
        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <Settings size={24} className="text-gray-600 mr-3" />
          Account Overview
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <p className="text-sm font-medium text-gray-500 mb-1">User ID</p>
            <p className="text-lg font-mono text-gray-900 break-all">
              {user?.id || "N/A"} {/* Added fallback for User ID */}
            </p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <p className="text-sm font-medium text-gray-500 mb-1">
              Account Created
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {user?.created_at ? formatIndianDate(user.created_at) : "N/A"}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Modals */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        userId={user?.id || ""}
      />
    </div>
  );
}
